import React, { Component, Fragment, useState, useEffect, useRef, useMemo, } from 'react';
import { FormGroup, Label, Modal, ModalHeader, ModalBody, ModalFooter, Table, Button, Input, Nav, NavItem, NavLink, } from 'reactstrap';
import numeral from 'numeral';
import { mapValues, omit, pickBy, mergeWith, last, chunk, uniqBy, orderBy, uniq, isEqual, get, sumBy, isEmpty, isNumber as _isNumber, groupBy, sortBy, keyBy, range, debounce, pick, } from 'lodash';
import { toast } from 'react-toastify';
import classnames from 'classnames';
import { Link } from 'react-router-dom';
import fileDownload from 'js-file-download';
import { unparse as unparseCsv, parse as parseCsv } from 'papaparse';
import { TextEncoder, TextDecoder } from 'text-encoding';
import qs from 'qs';
import Select from 'react-select';
import { useCounter, useToggle, useDebounce, useMap, useAsync, } from 'react-use';
import { useLocation, useHistory } from 'react-router-dom';
import { differenceInCalendarMonths, format as formatDate, startOfMonth, endOfMonth, addYears, addMonths, } from 'date-fns';
import { ResizableBox } from 'react-resizable';

import firebase, { functions } from '../../firebase';
import { batch, getCollectionData, getAllCollectionDataByChunk, } from '../../shared/firebase';
import { dimensionsOfCompany, enabledBudgetSubjectTypes, canUseForecast, } from '../../shared/models/company';
import { computeRelatedItems, } from '../../shared/models/customAccountItem';
import { mapKeysToJa, mapKeysFromJa, jas, } from '../../shared/texts';
import { dimensions, breakdownItemsLimitUnit, documentTypes, budgetSubjectTypes, screenQueryUncontrols, } from '../../shared/config';
import { startOfMonthByFiscalYears, existsInFiscalYears, fiscalYearOfPeriod, readFile, fullPathWithParams, trialItemsToRowItems, computeSegmentedTrialAmount, computeBudget, computeCustomAmount, pickSearch, filterRowsByAmount, closingDate, groupSegmentedTrials, groupSegmentedTrialSubItems, } from '../../utils';
import { canWriteNote, } from '../../abilities';
import useCollectionSubscription from '../hooks/useCollectionSubscription';
import useCollectionsFetch from '../hooks/useCollectionsFetch';
import useQueryParams from '../hooks/useQueryParams';
import useMainTableUtilities from '../hooks/useMainTableUtilities';
import HelpLink from '../HelpLink';
import TrialQueriesSection from '../TrialQueriesSection';
import ProgressButton from '../ProgressButton';
import AddButton from '../AddButton';
import EditButton from '../EditButton';
import DeleteButton from '../DeleteButton';
import UserDisplay from '../UserDisplay';
import ModelFormModal from '../modals/ModelFormModal';
import CompanyPage from '../hocs/CompanyPage';
import CommentsDropdown from '../CommentsDropdown';
import HoveredCommenter from '../HoveredCommenter';
import QuerySelector from '../QuerySelector';
import HoveredNoter from '../HoveredNoter';
import AccountItemDisplay from '../AccountItemDisplay';
import OverlayLoading from '../OverlayLoading';
import Row from './CompanyMonthlyBudgets/Row';
import ShareButton from '../ShareButton';
import JournalsSyncButton from '../JournalsSyncButton';
import AllCollapseButton from '../AllCollapseButton';
import BudgetButton from '../BudgetButton';

const suffix = documentType => ({ pl: '', cr: 'Cr' })[documentType];
const fetchFreeeSegmentedTrials = functions.httpsCallable('fetchFreeeSegmentedTrials', { timeout: 550000 });
const logCsvExport = functions.httpsCallable('logCsvExport');
const db = firebase.firestore();
const auth = firebase.auth();
const companiesRef = db.collection('companies');
const { keys, entries, } = Object;
const { abs } = Math;
const isFiniteNumber = _ => _isNumber(_) && isFinite(_);
const COLUMN_WIDTH = 150;
const AMOUNT_HEIGHT = '24px';
const metrics = {
  budgetAmount: { label: '予算' },
  comparedBudgetAmount: { label: '比較予算' },
  diffBetweenBudgets: { label: '予算差額' },
  amount: { label: '当期実績' },
  budgetDiff: { label: '差額' },
  achievementRate: { label: '達成率', isRate: true, },
  estimatedAmount: { label: '着地見込' },
  prevYearAmount: { label: '前期実績' },
  prevYearDiff: { label: '増減額' },
  prevYearDiffRate: { label: '増減率', isRate: true, },
};
const displayColumnTypes = {
  month: { label: '月', },
  quarter: { label: '四半期', },
  half: { label: '半期', },
};
const displayColumnTypeOptions = entries(displayColumnTypes).map(([k, v]) => ({ label: v.label, value: k }));

export default CompanyPage(CompanyMonthlyBudgets);

function CompanyMonthlyBudgets(props) {
  const { accountItemsOrderSetting, accountItems, customAccountItems, company, company: { sourceId: companySourceId }, user, members, period, targetMonth, match: { params: { companyId } } } = props;
  const history = useHistory();
  const location = useLocation();
  const queryParams = useQueryParams();
  // 見込機能はスタンダード・アドバンスのみ。プラン判定と事業所設定トグルの両方を満たす場合のみ有効。
  const useForecast = canUseForecast(company) && company.useForecast;
  // 見込利用時は「当期実績」を「実績/見込」に統合表示し、着地見込の別列は出さない。
  // 見込列(forecastAmount)は metrics 定義から外しているため、利用有無に関わらず独立表示しない
  const displayMetrics = useForecast
    ? omit({ ...metrics, amount: { ...metrics.amount, label: '実績/見込' } }, ['estimatedAmount'])
    : metrics;
  const displayMetricOptions = entries(displayMetrics).map(([k, v]) => ({ label: v.label, value: k }));
  const {
    tabKey = 'section__pl',
    dimension = 'none',
    budgetSubjectIds,
    budgetContainerId,
    comparedBudgetContainerId,
    itemNamesForFilter,
    subItemNamesForFilter,
    displayColumnTypes,
  } = queryParams;
  const {
    onClickComment,
    toggleCommenter,
    toggleInitiallyCommentRow,
    subRowsVisibilities,
    setAllSubRowsVisibilities,
    setSubRowsVisibilities,
    setHoveredCommenters,
    onRowsChanged,
  } = useMainTableUtilities();
  const [screenVersion, { inc: updateScreenVersion }] = useCounter();
  const [leftColumnWidth, setLeftColumnWidth] = useState(COLUMN_WIDTH * 2.5);
  const [allRows, setAllRows] = useState([]);
  const isSubFiltered = (queryParams.dimension || 'none') !== 'none' && queryParams.isSubFilter === '1';
  const relatedCustomAccountItems = useMemo(_ => customAccountItems.filter(_ => (_.dimension || 'none') === 'none' || _.dimension === dimension), [customAccountItems, dimension]);
  const openItemKeys = useMemo(_ => {
    if((queryParams.dimension || 'none') === 'none' || isSubFiltered) return [];
    
    // 予実月次推移画面に表示されるカスタム科目の関連 itemKeys を計算
    const customAccountItemsByName = keyBy(relatedCustomAccountItems, 'name');
    const customAccountItemKeys = relatedCustomAccountItems.flatMap(customAccountItem => {
      const relatedItems = computeRelatedItems(customAccountItem, customAccountItemsByName);
      return relatedItems.map(_ => ({
        accountItem: [_.value, 'none'].join('__'),
        accountCategory: ['none', _.value].join('__'),
      })[_.type]);
    });
    
    return uniq([...keys(pickBy(subRowsVisibilities, _ => _)), ...customAccountItemKeys]);
  }, [queryParams.dimension, subRowsVisibilities, isSubFiltered, relatedCustomAccountItems]);
  const [subRowsBreakdownItemsLimitRates, { set: setSubRowsBreakdownItemsLimitRates }] = useMap({});
  const [budgetSubjectType, documentType] = tabKey.split('__');
  const filterForScreen = _ => (_.accountItemCategory?.type === documentType && !_.accountItemCategory?.hidden) || _.isCustom;
  const rows = useMemo(_ => allRows.filter(filterForScreen), [allRows, documentType]);
  const { freeeCollectionName, label: budgetSubjectTypeLabel, idName: subjectIdName, trialsCollectionName, freeeSubjectIdsName, collectionName: subjectCollectionName, nameJa, budgetCollectionName, } = budgetSubjectTypes[budgetSubjectType];
  const dimensionOptions = entries(dimensionsOfCompany(company)).filter(_ => !['sections', 'segment1s', 'segment2s', 'segment3s'].filter(_ => (budgetSubjectType + 's') === _).includes(_[0])).map(([k, v]) => ({ label: v, value: k }));
  const [isCommentRowToggled, toggleCommentRowToggled] = useToggle();
  const [isSettingRows, toggleSettingRows] = useToggle(false);
  const [isFetchingLastTrials, toggleFetchingLastTrials] = useToggle();
  const [isExporting, toggleExporting] = useToggle();
  const [showsModal, toggleModal] = useToggle();
  const { start_date: startDate, use_industry_template: usesIndustry, } = fiscalYearOfPeriod(period, company.fiscalYears);
  const prevYearMonths = range(-12, 0).map(_ => endOfMonth(addMonths(startDate, _)));
  const currentYearMonths = range(0, 12).map(_ => endOfMonth(addMonths(startDate, _)));
  const budgetSubjects = useCollectionSubscription(company.ref.collection(subjectCollectionName), [budgetSubjectType]);
  const sortedBudgetSubjects = sortBy(budgetSubjects || [], _ => _.index != null ? _.index : (budgetSubjects || []).indexOf(_));
  const budgetContainers = useCollectionSubscription(companiesRef.doc(companyId).collection('budgetContainers').orderBy('index'));
  const freeeSubjects = useCollectionSubscription(companiesRef.doc(companyId).collection(freeeCollectionName), [budgetSubjectType]);
  const freeeSubjectsById = keyBy([{ id: 0, name: '未選択' }, ...freeeSubjects], 'id');
  const { value: budgets, } = useAsync(async () => {
    if(!budgetContainerId || isEmpty(budgetSubjectIds)) return [];

    return (await Promise.all(chunk(budgetSubjectIds, 10).map(async (budgetSubjectIds) => {
      return getCollectionData(companiesRef.doc(companyId).collection('budgets').where('period', '==', period).where('budgetContainerId', '==', budgetContainerId).where('budgetSubjectType', '==', budgetSubjectType).where('budgetSubjectId', 'in', budgetSubjectIds).where('dimension', '==', dimension));
    }))).flat();
  }, [screenVersion, period, budgetContainerId, budgetSubjectIds, dimension]);
  const budgetsGroupedByClosingDate = groupBy(budgets, _ => formatDate(_.closingDate, 'YYYYMM'));
  const budgetsGroupedByItemKey = groupBy(budgets, 'itemKey');
  const { value: comparedBudgets, } = useAsync(async () => {
    if(!comparedBudgetContainerId || isEmpty(budgetSubjectIds)) return [];

    return (await Promise.all(chunk(budgetSubjectIds, 10).map(async (budgetSubjectIds) => {
      return getCollectionData(companiesRef.doc(companyId).collection('budgets').where('period', '==', period).where('budgetContainerId', '==', comparedBudgetContainerId).where('budgetSubjectType', '==', budgetSubjectType).where('budgetSubjectId', 'in', budgetSubjectIds).where('dimension', '==', dimension));
    }))).flat();
  }, [screenVersion, period, comparedBudgetContainerId, budgetSubjectIds, dimension]);
  const comparedBudgetsGroupedByClosingDate = useMemo(_ => groupBy(comparedBudgets, _ => formatDate(_.closingDate, 'YYYYMM')), [comparedBudgets]);
  const comparedBudgetsGroupedItemKey = groupBy(comparedBudgets, 'itemKey');
  const { value: forecasts, } = useAsync(async () => {
    if(!useForecast || isEmpty(budgetSubjectIds)) return [];

    return (await Promise.all(chunk(budgetSubjectIds, 10).map(async (budgetSubjectIds) => {
      return getCollectionData(companiesRef.doc(companyId).collection('budgets').where('period', '==', period).where('budgetContainerId', '==', 'forecast').where('budgetSubjectType', '==', budgetSubjectType).where('budgetSubjectId', 'in', budgetSubjectIds).where('dimension', '==', dimension));
    }))).flat();
  }, [screenVersion, period, budgetSubjectIds, dimension, useForecast]);
  const forecastsGroupedByItemKey = groupBy(forecasts, 'itemKey');
  const trialsRef = (documentType) => {
    return company.ref
      .collection(trialsCollectionName + suffix(documentType))
      .where('dimension', '==', 'none')
      .where('closingDate', '>=', formatDate(prevYearMonths[0], 'YYYY-MM-DD'))
      .where('closingDate', '<=', formatDate(last(currentYearMonths), 'YYYY-MM-DD'));
  };
  const plTrials = useCollectionSubscription(trialsRef('pl'), [tabKey, dimension, period]);
  const crTrials = useCollectionSubscription(trialsRef('cr'), [tabKey, dimension, period]);
  const trials = useMemo(_ => [...plTrials, ...crTrials], [plTrials, crTrials]);
  const trialsGroupedByClosingDate = groupBy(trials, _ => formatDate(_.closingDate, 'YYYYMM'));
  const trialSubjectRowsGroupedByItemKey = groupSegmentedTrials(trials.flatMap(t => (t[freeeCollectionName] || []).map(_ => ({ ...t, ...omit(_, 'id'), subjectId: _.id, }))));

  const subItemCollectionName = trialsCollectionName + 'SubItemChunks__' + dimension;
  const refs = useMemo(_ =>
    isSubFiltered ? (
      [company.ref.collection(subItemCollectionName)]
    ) : chunk(openItemKeys, 10).map(_ => company.ref.collection(subItemCollectionName).where('itemKey', 'in', _))
  , [isSubFiltered, subItemCollectionName, openItemKeys]);
  const trialSubItemChunks = useCollectionsFetch(refs.map((ref) => {
    return ref
      .where('closingDate', '>=', formatDate(prevYearMonths[0], 'YYYY-MM-DD'))
      .where('closingDate', '<=', formatDate(last(currentYearMonths), 'YYYY-MM-DD'));
  }), [refs, period]);
  const trialSubItems = useMemo(_ => trialSubItemChunks.flatMap(_ => _.subItems.map(__ => ({ ..._, ...__ }))), [trialSubItemChunks]);
  const trialSubItemsGroupedByClosingDate = groupBy(trialSubItems, _ => formatDate(_.closingDate, 'YYYYMM'));
  const trialSubItemsGroupedByItemKey = groupSegmentedTrialSubItems(trialSubItems);

  const comments = useCollectionSubscription(companiesRef.doc(companyId).collection('comments').where('queryKey', '==', budgetCollectionName).orderBy('createdAt'));
  const commentsGroupedByCommenteeKey = groupBy(comments, 'commenteeKey');
  const notes = useCollectionSubscription(companiesRef.doc(companyId).collection('notes').where('queryKey', '==', budgetCollectionName));
  const notesByKey = keyBy(notes, 'noteKey');
  const toggleTab = (tabKey) => {
    const [targetBudgetSubjectType] = tabKey.split('__');
    history.replace(location.pathname + '?' + qs.stringify({ ...queryParams, tabKey, ...(budgetSubjectType !== targetBudgetSubjectType && { budgetContainerId: null, budgetSubjectIds: null, }), }));
  };
  const budgetSubjectOptions = sortedBudgetSubjects.map(_ => ({ label: _.name, value: _.id }));
  const budgetContainerOptions = budgetContainers.map(_ => ({ label: _.name, value: _.id }));
  const itemNameOptions = uniq(rows.map(_ => _.itemName)).map(_ => ({ label: _, value: _ }));
  const subItemNameOptions = uniq(rows.map(_ => _.subItemName).filter(_ => _)).map(_ => ({ label: _, value: _ }));
  const generateMonthsItems = (options) => {
    const { budgetsGroupedByClosingDate, comparedBudgetsGroupedByClosingDate, } = options;
    return [prevYearMonths, currentYearMonths].map((months) => {
      return months.map((closingDate) => {
        const balances = trialsGroupedByClosingDate[formatDate(closingDate, 'YYYYMM')] || [];
        const budgets = budgetsGroupedByClosingDate[formatDate(closingDate, 'YYYYMM')] || [];
        const comparedBudgets = comparedBudgetsGroupedByClosingDate[formatDate(closingDate, 'YYYYMM')] || [];
        const trialSubItems = (options?.trialSubItemsGroupedByClosingDate || trialSubItemsGroupedByClosingDate)[formatDate(closingDate, 'YYYYMM')] || [];
        const exists = !isEmpty(balances);
        return {
          closingDate,
          exists,
          balances,
          balancesByItemKey: keyBy(balances, 'itemKey'),
          budgets,
          budgetsGroupedByItemKey: groupBy(budgets, 'itemKey'),
          comparedBudgets,
          comparedBudgetsGroupedByItemKey: groupBy(comparedBudgets, 'itemKey'),
          trialSubItemsGroupedByItemKey: groupBy(trialSubItems, 'itemKey'),
          dimension,
        };
      });
    });
  };
  const monthsItems = generateMonthsItems({ budgetsGroupedByClosingDate, comparedBudgetsGroupedByClosingDate, });
  const prevYearMonthItemsByMonth = keyBy(monthsItems[0], _ => formatDate(_.closingDate, 'MM'));
  const budgetSubjectsById = keyBy(budgetSubjects, 'id');
  const selectedBudgetSubjects = (budgetSubjectIds || []).map(_ => budgetSubjectsById[_]);
  const generateRows = (options) => {
    const { monthsItems, budgetSubjects, prevYearMonthItemsByMonth, budgetsGroupedByItemKey, comparedBudgetsGroupedItemKey, forecastsGroupedByItemKey, } = options;
    const trialSubItemsGroupedByItemKey = options?.trialSubItems && groupSegmentedTrialSubItems(options.trialSubItems);
    const items = trialItemsToRowItems(monthsItems.flat(), { targetMonth, accountItems, customAccountItems: relatedCustomAccountItems, screenType: 'sectionBudgets', documentType, itemType: documentType, dimension, budgetSubjectsFreeeName: freeeCollectionName, sortsAllPlAndCrCategories: true, accountItemsOrderSetting, })
    const mainItems = items.filter(_ => !_.isCategory && !_.isSubRow && !_.isCustom);
    const mainItemsByItemKey = keyBy(mainItems, 'itemKey');
    const rows = items
      .map(itemToRow.bind(null, mainItems, mainItemsByItemKey, { monthsItems, budgetSubjects, prevYearMonthItemsByMonth, budgetsGroupedByItemKey, comparedBudgetsGroupedItemKey, forecastsGroupedByItemKey, trialSubItemsGroupedByItemKey, }))
      .filter(_ => _.columns.some(_ => abs(_.amount) > 0 || abs(_.budgetAmount) > 0 || abs(_.comparedBudgetAmount) > 0 || abs(_.prevYearAmount) > 0));
    return entries(groupBy(
      Object.values(groupBy(rows, 'mainRowItemKey')).map(([mainItem, ...subItems]) => {
        return [mainItem, ...orderBy(subItems, [_ => Math.abs(_.monthColumnsByYearMonth[targetMonth].amount), _ => Math.abs(_.monthColumnsByYearMonth[targetMonth].budgetAmount)], ['desc', 'desc'])];
      }).flat()
    , 'itemKey'))
      .reduce((x, [itemKey, [mainRow, ...subRows]]) => {
        const _subRows = subRows.map((row, i) => {
          return {
            ...row,
            breakDownItemIndex: i,
            subItemsCount: subRows.length,
          };
        });
        return [
          ...x,
          { ...mainRow, subRows: _subRows },
          ..._subRows
        ];
      }, []);
  };
  const computeOverMonth = (date) => {
    return date > endOfMonth(targetMonth.toString().replace(/\d{2}$/, _ => '-' + _));
  };
  const isPastMonth = (date) => {
    return date <= endOfMonth(targetMonth.toString().replace(/\d{2}$/, _ => '-' + _));
  };
  const quarters = chunk(currentYearMonths, 3);
  const halfs = chunk(currentYearMonths, 6);
  const itemToRow = (mainItems, mainItemsByItemKey, options, item) => {
    const { monthsItems, budgetSubjects, prevYearMonthItemsByMonth, budgetsGroupedByItemKey, comparedBudgetsGroupedItemKey, forecastsGroupedByItemKey: _forecastsGroupedByItemKey = forecastsGroupedByItemKey, } = options;
    const _trialSubItemsGroupedByItemKey = options.trialSubItemsGroupedByItemKey || trialSubItemsGroupedByItemKey;
    const { isCustom = false, accountItemCategory, itemName, itemKey, isSubRow, subItemName, hierarchyLevel, accountItemName, displayExpression = '0,0', } = item;
    const accountCategoryName = accountItemName ? null : item.account_category_name || item.accountCategoryName;
    const isCategory = isEmpty(accountItemName);
    const rowKey = (isCustom ? 'cus__' : '') + (isCategory ? 'cat__' : '') + ((dimension !== 'none' && isSubRow) ? `${itemName}__${dimension}__${subItemName}` : itemName);
    const mainRowItemKey = isCustom ? itemKey : item.mainRowItemKey;
    const rowName = subItemName || itemName;
    const monthColumns = monthsItems[1].map((monthItem, i) => {
      const { closingDate, } = monthItem;
      const isOverMonth = computeOverMonth(closingDate);
      const isPast = isPastMonth(closingDate);
      // 見込利用時、将来月のamountは実績ではなく見込なので仕訳帳へのリンクを出さない
      const isForecastCell = useForecast && !isPast;
      const prevYearMonthItem = prevYearMonthItemsByMonth[formatDate(closingDate, 'MM')];
      const { closingDate: prevYearClosingDate, } = prevYearMonthItem;
      const yearMonth = formatDate(closingDate, 'YYYYMM');
      const key = `${rowKey}_${yearMonth}`;
      const budgetSubjectRows = budgetSubjects.map((budgetSubject) => {
        const key = `${rowKey}_${yearMonth}_${budgetSubject?.id}`;
        const freeeSubjects = budgetSubject?.[freeeSubjectIdsName].map(_ => freeeSubjectsById[_]).filter(_ => _);
        const commenteeKey = [rowKey, budgetSubject?.id, yearMonth].join('_');
        const noteKey = [rowKey, budgetSubject?.id, yearMonth].join('_');
        const amountBase = computeSegmentedTrialAmount(budgetSubjectType, item, trialSubjectRowsGroupedByItemKey, _trialSubItemsGroupedByItemKey, dimension, [closingDate], customAccountItems, budgetSubject?.[freeeSubjectIdsName], [budgetSubject?.id]);
        const prevYearAmountBase = computeSegmentedTrialAmount(budgetSubjectType, item, trialSubjectRowsGroupedByItemKey, _trialSubItemsGroupedByItemKey, dimension, [prevYearClosingDate], customAccountItems, budgetSubject?.[freeeSubjectIdsName], [budgetSubject?.id]);
        const prevYearAmount = isOverMonth ? 0 : prevYearAmountBase;
        const budgetAmount = computeBudget(budgetSubjectType, item, budgetsGroupedByItemKey, dimension, [closingDate], customAccountItems, [budgetSubject?.id], mainItems, mainItemsByItemKey);
        const comparedBudgetAmount = computeBudget(budgetSubjectType, item, comparedBudgetsGroupedItemKey, dimension, [closingDate], customAccountItems, [budgetSubject?.id], mainItems, mainItemsByItemKey);
        // 見込: 将来月の見込が未入力(0)なら予算で補完（着地見込と同じ挙動）
        const forecastAmountBase = useForecast ? computeBudget(budgetSubjectType, item, _forecastsGroupedByItemKey, dimension, [closingDate], customAccountItems, [budgetSubject?.id], mainItems, mainItemsByItemKey, 'forecast') : 0;
        const forecastAmount = useForecast ? (forecastAmountBase || budgetAmount) : 0;
        // 実績/見込: 過去月=実績、将来月=見込(未入力なら予算)
        const amount = useForecast
          ? (isPast ? amountBase : forecastAmount)
          : (isOverMonth ? 0 : amountBase);
        const diffBetweenBudgets = budgetAmount - comparedBudgetAmount;
        const budgetDiff = useForecast
          ? amount - budgetAmount
          : (isOverMonth ? null : amount - budgetAmount);
        const achievementRate = amount / budgetAmount;
        const estimatedAmount = useForecast
          ? amount
          : (isOverMonth ? budgetAmount : amountBase);
        const prevYearDiff = amount - prevYearAmount;
        const prevYearDiffRate = prevYearDiff / prevYearAmount;
        const note = (notesByKey[noteKey] || {}).value;
        return { budgetSubject, key, isForecastCell, commenteeKeys: [commenteeKey], noteKeys: [noteKey], closingDate, yearMonth, amount, budgetAmount, comparedBudgetAmount, diffBetweenBudgets, budgetDiff, achievementRate, estimatedAmount, prevYearAmount, prevYearDiff, prevYearDiffRate, note, freeeSubjects, amountBase, };
      });
      // NOTE: カスタム科目（比率系など）は部門ごとの値を足すと 率+率 になってしまうため、
      // 全部門をまとめて1回で計算し直す（分子・分母を合算してから割る）。通常科目は合算と一致する。
      const combinedCustomAmounts = isCustom ? (_ => {
        const allFreeeSubjectIds = budgetSubjects.flatMap(_ => _?.[freeeSubjectIdsName] || []);
        const allBudgetSubjectIds = budgetSubjects.map(_ => _?.id);
        const amountBase = computeSegmentedTrialAmount(budgetSubjectType, item, trialSubjectRowsGroupedByItemKey, _trialSubItemsGroupedByItemKey, dimension, [closingDate], customAccountItems, allFreeeSubjectIds, allBudgetSubjectIds);
        const prevYearAmountBase = computeSegmentedTrialAmount(budgetSubjectType, item, trialSubjectRowsGroupedByItemKey, _trialSubItemsGroupedByItemKey, dimension, [prevYearClosingDate], customAccountItems, allFreeeSubjectIds, allBudgetSubjectIds);
        const prevYearAmount = isOverMonth ? 0 : prevYearAmountBase;
        const budgetAmount = computeBudget(budgetSubjectType, item, budgetsGroupedByItemKey, dimension, [closingDate], customAccountItems, allBudgetSubjectIds, mainItems, mainItemsByItemKey);
        const comparedBudgetAmount = computeBudget(budgetSubjectType, item, comparedBudgetsGroupedItemKey, dimension, [closingDate], customAccountItems, allBudgetSubjectIds, mainItems, mainItemsByItemKey);
        const forecastAmountBase = useForecast ? computeBudget(budgetSubjectType, item, _forecastsGroupedByItemKey, dimension, [closingDate], customAccountItems, allBudgetSubjectIds, mainItems, mainItemsByItemKey, 'forecast') : 0;
        const forecastAmount = useForecast ? (forecastAmountBase || budgetAmount) : 0;
        const amount = useForecast
          ? (isPast ? amountBase : forecastAmount)
          : (isOverMonth ? 0 : amountBase);
        const budgetDiff = useForecast
          ? amount - budgetAmount
          : (isOverMonth ? null : amount - budgetAmount);
        const estimatedAmount = useForecast
          ? amount
          : (isOverMonth ? budgetAmount : amountBase);
        return {
          amountBase, amount, prevYearAmount, budgetAmount, comparedBudgetAmount,
          diffBetweenBudgets: budgetAmount - comparedBudgetAmount,
          budgetDiff, estimatedAmount,
          prevYearDiff: amount - prevYearAmount,
        };
      })() : null;
      const sumOrCombined = key => combinedCustomAmounts ? combinedCustomAmounts[key] : sumBy(budgetSubjectRows, key);
      const _totalAmountInner = sumOrCombined('amount');
      const _totalBudgetAmountInner = sumOrCombined('budgetAmount');
      const _totalPrevYearAmountInner = sumOrCombined('prevYearAmount');
      const _totalPrevYearDiffInner = sumOrCombined('prevYearDiff');
      const budgetSubjectsSummaryRow = {
        key: `${rowKey}_${yearMonth}_summary`,
        isSummaryColumn: true,
        label: '合計',
        closingDate,
        yearMonth,
        amount: _totalAmountInner,
        budgetAmount: _totalBudgetAmountInner,
        comparedBudgetAmount: sumOrCombined('comparedBudgetAmount'),
        diffBetweenBudgets: sumOrCombined('diffBetweenBudgets'),
        budgetDiff: sumOrCombined('budgetDiff'),
        achievementRate: _totalAmountInner / _totalBudgetAmountInner,
        estimatedAmount: sumOrCombined('estimatedAmount'),
        prevYearAmount: _totalPrevYearAmountInner,
        prevYearDiff: _totalPrevYearDiffInner,
        prevYearDiffRate: _totalPrevYearDiffInner / _totalPrevYearAmountInner,
      };
      const freeeSubjects = budgetSubjectRows.flatMap(_ => _.freeeSubjects);
      const commenteeKeys = budgetSubjectRows.map(_ => _.commenteeKeys[0]);
      const noteKeys = budgetSubjectRows.map(_ => _.noteKeys[0]);
      const [amountBase, amount, prevYearAmount, budgetAmount, comparedBudgetAmount, diffBetweenBudgets, budgetDiff, estimatedAmount, prevYearDiff] = ['amountBase', 'amount', 'prevYearAmount', 'budgetAmount', 'comparedBudgetAmount', 'diffBetweenBudgets', 'budgetDiff', 'estimatedAmount', 'prevYearDiff'].map(sumOrCombined);
      const achievementRate = amount / budgetAmount;
      const prevYearDiffRate = prevYearDiff / prevYearAmount;
      const notes = noteKeys.map(_ => (notesByKey[_] || {}).value);
      return { key, columnType: 'month', isForecastCell, csvLabel: '_' + yearMonth, commenteeKeys, noteKeys, closingDate, yearMonth, amount, budgetAmount, comparedBudgetAmount, diffBetweenBudgets, budgetDiff, achievementRate, estimatedAmount, prevYearAmount, prevYearDiff, prevYearDiffRate, notes, note: notes.join('\n'), freeeSubjects, amountBase, budgetSubjectRows, budgetSubjectsSummaryRow, };
    });
    const monthColumnsByYearMonth = keyBy(monthColumns, 'yearMonth');
    const prevYearMonthsByMm = keyBy(prevYearMonths, _ => formatDate(_, 'MM'));
    const computeSummaryColumn = (columnType, termMonths, termIndex) => {
      const notOverTermMonths = termMonths.filter(_ => !computeOverMonth(_));
      const notOverPrevYearTermMonths = notOverTermMonths.map(_ => prevYearMonthsByMm[formatDate(_, 'MM')]);
      const termMonthColumns = termMonths.map(_ => monthColumnsByYearMonth[formatDate(_, 'YYYYMM')]);
      const key = `${rowKey}_${termMonths.map(_ => formatDate(_, 'YYYYMM')).join(',')}`;
      // 実績/見込: useForecast時は月セルの統合amountを合算（過去=実績、将来=見込）
      // NOTE: ただしカスタム科目（比率系など）は完成した率同士を足せないため、
      // 計算式の各項（分子・分母）の段階で 過去月の実績 + 将来月の見込 を合算し、除算は最後に1回だけ行う。
      const amount = useForecast
        ? (isCustom ? computeCustomAmount(item, termMonths, customAccountItems, budgetSubjects.map(_ => _.id), 'performance', (amountType, _item) => {
          const pastMonths = termMonths.filter(isPastMonth);
          const futureMonths = termMonths.filter(_ => !isPastMonth(_));
          const pastAmount = isEmpty(pastMonths) ? 0 : computeSegmentedTrialAmount(budgetSubjectType, _item, trialSubjectRowsGroupedByItemKey, _trialSubItemsGroupedByItemKey, dimension, pastMonths, customAccountItems, budgetSubjects.flatMap(_ => _?.[freeeSubjectIdsName]), budgetSubjects.map(_ => _.id));
          if(isEmpty(futureMonths)) return pastAmount;
          const budgetItem = _item.isCustomMain ? mainItemsByItemKey[_item.itemKey] : _item;
          const futureForecast = !budgetItem ? 0 : (
            computeBudget(budgetSubjectType, budgetItem, _forecastsGroupedByItemKey, dimension, futureMonths, customAccountItems, budgetSubjects.map(_ => _.id), mainItems, mainItemsByItemKey, 'forecast')
              || computeBudget(budgetSubjectType, budgetItem, budgetsGroupedByItemKey, dimension, futureMonths, customAccountItems, budgetSubjects.map(_ => _.id), mainItems, mainItemsByItemKey)
          );
          return pastAmount + futureForecast;
        }) : sumBy(termMonthColumns, _ => _?.amount || 0))
        : computeSegmentedTrialAmount(budgetSubjectType, item, trialSubjectRowsGroupedByItemKey, _trialSubItemsGroupedByItemKey, dimension, notOverTermMonths, customAccountItems, budgetSubjects.flatMap(_ => _?.[freeeSubjectIdsName]), budgetSubjects.map(_ => _.id));
      const prevYearAmount = computeSegmentedTrialAmount(budgetSubjectType, item, trialSubjectRowsGroupedByItemKey, _trialSubItemsGroupedByItemKey, dimension, notOverPrevYearTermMonths, customAccountItems, budgetSubjects.flatMap(_ => _?.[freeeSubjectIdsName]), budgetSubjects.map(_ => _.id));
      const budgetAmount = computeBudget(budgetSubjectType, item, budgetsGroupedByItemKey, dimension, termMonths, customAccountItems, budgetSubjects.map(_ => _.id), mainItems, mainItemsByItemKey);
      const comparedBudgetAmount = computeBudget(budgetSubjectType, item, comparedBudgetsGroupedItemKey, dimension, termMonths, customAccountItems, budgetSubjects.map(_ => _.id), mainItems, mainItemsByItemKey);
      const diffBetweenBudgets = budgetAmount - comparedBudgetAmount;
      const budgetDiff = useForecast ? (amount - budgetAmount) : (isCustom ? (termMonths.some(computeOverMonth) ? null : amount - budgetAmount) : amount - budgetAmount);
      const achievementRate = amount / budgetAmount;
      const estimatedAmount = useForecast ? amount : (isCustom ? (termMonths.some(computeOverMonth) ? null : amount) : sumBy(termMonthColumns, 'estimatedAmount'));
      const prevYearDiff = amount - prevYearAmount;
      const prevYearDiffRate = prevYearDiff / prevYearAmount;
      return { key, isSummaryColumn: true, csvLabel: ({ quarter: `${termIndex+1}Q`, half: `${({ 0: '上', 1: '下' })[termIndex]}半期` })[columnType], columnType, closingDate, amount, budgetAmount, comparedBudgetAmount, diffBetweenBudgets, budgetDiff, achievementRate, estimatedAmount, prevYearAmount, prevYearDiff, prevYearDiffRate, };
    };
    const quarterColumns = quarters.map(computeSummaryColumn.bind(null, 'quarter'));
    const halfColumns = halfs.map(computeSummaryColumn.bind(null, 'half'));
    const columns = chunk(chunk(monthColumns, 3), 2).flatMap((halfQuarters, halfIndex) => [...halfQuarters.flatMap((monthColumns, quarterIndex) => [...monthColumns, quarterColumns[halfIndex * 2 + quarterIndex]]), halfColumns[halfIndex]]);
    // NOTE: カスタム科目（比率系など）は月次の値を足すと 率+率 になってしまうため、
    // 通期をまとめて1回で計算し直す（四半期・半期列と同じ computeSummaryColumn を利用）。
    const yearSummaryColumn = isCustom ? computeSummaryColumn('year', currentYearMonths, 0) : null;
    const totalOrYear = (key, monthColumnKey = key) => isCustom ? yearSummaryColumn[key] : sumBy(monthColumns, monthColumnKey);
    const totalAmount = totalOrYear('amount');
    const totalBudgetAmount = totalOrYear('budgetAmount');
    const totalBudgetDiff = totalOrYear('budgetDiff');
    const totalComparedBudgetAmount = totalOrYear('comparedBudgetAmount');
    const totalDiffBetweenBudgets = totalOrYear('diffBetweenBudgets');
    const totalEstimatedAmount = totalOrYear('estimatedAmount');
    const totalPrevYearAmount = totalOrYear('prevYearAmount');
    const totalPrevYearDiff = totalOrYear('prevYearDiff');
    const summaryColumns = [
      {
        isSummaryColumn: true,
        name: 'sum',
        key: `${rowKey}_sum`,
        amount: totalAmount,
        budgetAmount: totalBudgetAmount,
        comparedBudgetAmount: totalComparedBudgetAmount,
        diffBetweenBudgets: totalDiffBetweenBudgets,
        budgetDiff: totalBudgetDiff,
        achievementRate: totalAmount / totalBudgetAmount,
        estimatedAmount: totalEstimatedAmount,
        prevYearAmount: totalPrevYearAmount,
        prevYearDiff: totalPrevYearDiff,
        prevYearDiffRate: totalPrevYearDiff / totalPrevYearAmount,
      },
    ];
    const comments = monthColumns.flatMap(_ => _.commenteeKeys.flatMap(_ => commentsGroupedByCommenteeKey[_] || []));
    return {
      ...item,
      budgetSubjects,
      accountItemCategory,
      accountItemName,
      accountCategoryName,
      hierarchyLevel,
      isCategory,
      isCustom,
      itemName,
      itemKey,
      mainRowItemKey,
      key: rowKey,
      subItemName,
      isSubRow,
      rowName,
      displayExpression,
      monthColumns,
      monthColumnsByYearMonth,
      columns,
      summaryColumns,
      comments,
    };
  }
  const rowsDependencies = [budgetSubjectIds, budgetContainerId, comparedBudgetContainerId, trials, trialSubItems, budgets, forecasts, targetMonth, comparedBudgets, documentType];
  useEffect(_ => toggleSettingRows(true), rowsDependencies);
  useDebounce(async () => {
    setAllRows(generateRows({ monthsItems, budgetSubjects: selectedBudgetSubjects, prevYearMonthItemsByMonth, budgetsGroupedByItemKey, comparedBudgetsGroupedItemKey, forecastsGroupedByItemKey, }));
    toggleSettingRows(false);
  }, 1000, rowsDependencies);

  const filterRows = (rows) => {
    let filteredRows = rows;
    if(!isEmpty(itemNamesForFilter)) {
      filteredRows = filteredRows.filter(_ => itemNamesForFilter.includes(_.itemName));
    }
    if(!isSubFiltered) {
      entries(displayMetrics).map(([metric, { isRate }]) => {
        filteredRows = filterRowsByAmount(filteredRows, queryParams, metric, _ => _.columns.find(_ => _.yearMonth === targetMonth.toString())?.[metric] * (isRate ? 100 : 1));
      });
    } else {
      filteredRows = entries(groupBy(filteredRows, 'itemKey'))
        .map(([itemKey, [mainRow, ...subRows]]) => {
          let filteredSubRows = subRows;
          if(!isEmpty(subItemNamesForFilter)) {
            filteredSubRows = filteredSubRows.filter(row => subItemNamesForFilter.some(_ => row.subItemName === _));
          }
          isSubFiltered && entries(displayMetrics).map(([metric, { isRate }]) => {
            filteredSubRows = filterRowsByAmount(filteredSubRows, queryParams, metric, _ => _.columns.find(_ => _.yearMonth === targetMonth.toString())?.[metric] * (isRate ? 100 : 1));
          });
          return {
            mainRow: (
              subRows.length === 0 ? mainRow : {
                ...mainRow,
                columns: mainRow.columns.map((column, i) => {
                  const amount = !isSubFiltered ? column.amount : sumBy(filteredSubRows, _ => _.columns[i].amount);
                  const prevYearAmount = !isSubFiltered ? column.prevYearAmount : sumBy(filteredSubRows, _ => _.columns[i].prevYearAmount);
                  const budgetAmount = !isSubFiltered ? column.budgetAmount : sumBy(filteredSubRows, _ => _.columns[i].budgetAmount);
                  const comparedBudgetAmount = !isSubFiltered ? column.comparedBudgetAmount : sumBy(filteredSubRows, _ => _.columns[i].comparedBudgetAmount);
                  const diffBetweenBudgets = budgetAmount - comparedBudgetAmount;
                  const budgetDiff = amount - budgetAmount;
                  const achievementRate = amount / budgetAmount;
                  const estimatedAmount = !isSubFiltered ? column.estimatedAmount : sumBy(filteredSubRows, _ => _.columns[i].estimatedAmount);
                  const prevYearDiff = amount - prevYearAmount;
                  const prevYearDiffRate = prevYearDiff / prevYearAmount;
                  return {
                    ...column,
                    amount,
                    prevYearAmount,
                    budgetAmount,
                    comparedBudgetAmount,
                    diffBetweenBudgets,
                    budgetDiff,
                    achievementRate,
                    estimatedAmount,
                    prevYearDiff,
                    prevYearDiffRate,
                  };
                }),
                summaryColumns: mainRow.summaryColumns.map((column, i) => {
                  const amount = !isSubFiltered ? column.amount : sumBy(filteredSubRows, _ => _.summaryColumns[i].amount);
                  const prevYearAmount = !isSubFiltered ? column.prevYearAmount : sumBy(filteredSubRows, _ => _.summaryColumns[i].prevYearAmount);
                  const budgetAmount = !isSubFiltered ? column.budgetAmount : sumBy(filteredSubRows, _ => _.summaryColumns[i].budgetAmount);
                  const comparedBudgetAmount = !isSubFiltered ? column.comparedBudgetAmount : sumBy(filteredSubRows, _ => _.summaryColumns[i].comparedBudgetAmount);
                  const diffBetweenBudgets = budgetAmount - comparedBudgetAmount;
                  const budgetDiff = amount - budgetAmount;
                  const achievementRate = amount / budgetAmount;
                  const estimatedAmount = !isSubFiltered ? column.estimatedAmount : sumBy(filteredSubRows, _ => _.summaryColumns[i].estimatedAmount);
                  const prevYearDiff = amount - prevYearAmount;
                  const prevYearDiffRate = prevYearDiff / prevYearAmount;
                  return {
                    ...column,
                    amount,
                    prevYearAmount,
                    budgetAmount,
                    comparedBudgetAmount,
                    diffBetweenBudgets,
                    budgetDiff,
                    achievementRate,
                    estimatedAmount,
                    prevYearDiff,
                    prevYearDiffRate,
                  };
                }),
              }
            ),
            subRows: filteredSubRows.map((row, i) => {
              return {
                ...row,
                breakDownItemIndex: i,
                subItemsCount: filteredSubRows.length,
              };
            }),
          };
        })
        .filter(_ => !isSubFiltered || _.subRows.length > 0)
        .reduce((x, y) => [...x, y.mainRow, ...y.subRows], []);
    }
    return filteredRows;
  };
  const filteredRows = filterRows(rows);

  const onClickExport = async (isAll) => {
    toggleExporting(true);
    const metricsForFilter = (queryParams.metrics || keys(displayMetrics)).filter(_ => displayMetrics[_]);
    const isComparing = !isEmpty(comparedBudgetContainerId);
    const encoder = new TextEncoder('Shift_JIS', { NONSTANDARD_allowLegacyEncoding: true });
    const trialSubItems = dimension !== 'none' && (await getAllCollectionDataByChunk(company.ref
      .collection(subItemCollectionName)
      .where('closingDate', '>=', formatDate(prevYearMonths[0], 'YYYY-MM-DD'))
      .where('closingDate', '<=', formatDate(last(currentYearMonths), 'YYYY-MM-DD'))
    )).flatMap(_ => _.subItems.map(__ => ({ ..._, ...__ })));
    const trialSubItemsGroupedByClosingDate = groupBy(trialSubItems, _ => formatDate(_.closingDate, 'YYYYMM'));
    const allRows = isAll ? await (async () => {
      const budgets = await getCollectionData(companiesRef.doc(companyId).collection('budgets').where('period', '==', period).where('budgetContainerId', '==', budgetContainerId).where('budgetSubjectType', '==', budgetSubjectType).where('dimension', '==', dimension));
      const budgetsGroupedByItemKey = groupBy(budgets, 'itemKey');
      const budgetsGroupedByClosingDate = groupBy(budgets, _ => formatDate(_.closingDate, 'YYYYMM'));
      const comparedBudgets = comparedBudgetContainerId ? await getCollectionData(companiesRef.doc(companyId).collection('budgets').where('period', '==', period).where('budgetContainerId', '==', comparedBudgetContainerId).where('budgetSubjectType', '==', budgetSubjectType).where('dimension', '==', dimension)) : [];
      const comparedBudgetsGroupedByClosingDate = groupBy(comparedBudgets, _ => formatDate(_.closingDate, 'YYYYMM'));
      const comparedBudgetsGroupedItemKey = groupBy(comparedBudgets, 'itemKey');
      const allForecasts = useForecast ? await getCollectionData(companiesRef.doc(companyId).collection('budgets').where('period', '==', period).where('budgetContainerId', '==', 'forecast').where('budgetSubjectType', '==', budgetSubjectType).where('dimension', '==', dimension)) : [];
      const forecastsGroupedByItemKey = groupBy(allForecasts, 'itemKey');
      return sortedBudgetSubjects.map((budgetSubject) => {
        const monthsItems = generateMonthsItems({ budgetsGroupedByClosingDate, comparedBudgetsGroupedByClosingDate, trialSubItemsGroupedByClosingDate, });
        const prevYearMonthItemsByMonth = keyBy(monthsItems[0], _ => formatDate(_.closingDate, 'MM'));
        return generateRows({ monthsItems, budgetSubjects: [budgetSubject], prevYearMonthItemsByMonth, budgetsGroupedByItemKey, comparedBudgetsGroupedItemKey, forecastsGroupedByItemKey, trialSubItems, }).map(_ => ({ ..._, budgetSubject }));
      }).flat();
    })() : generateRows({ monthsItems, budgetSubjects: selectedBudgetSubjects, prevYearMonthItemsByMonth, budgetsGroupedByItemKey, comparedBudgetsGroupedItemKey, forecastsGroupedByItemKey, trialSubItems, });
    const data = filterRows(allRows.filter(filterForScreen)).map((row) => {
      return [...metricsForFilter, ...(row.columns.every(_ => isEmpty(_.notes)) ? [] : ['note'])].map((rowType) => {
        return {
          budgetSubjectName: row.budgetSubjects?.map(_ => _.name).join(','),
          ...pick(row, ['itemName', 'subItemName']),
          rowType: jas[rowType],
          ...(
            row.columns.filter(_ => isEmpty(displayColumnTypes) || displayColumnTypes.includes(_.columnType)).reduce((x, y) => {
              return {
                ...x,
                [y.csvLabel]: y[rowType],
              };
            }, {})
          ),
          ...(
            row.summaryColumns.reduce((x, y) => {
              const { name } = y;
              return {
                ...x,
                [name]: y[rowType],
              };
            }, {})
          ),
        };
      });
    }).reduce((x, y) => [...x, ...y], []);
    const fileContent = encoder.encode(unparseCsv(data.map(_ => mapKeysToJa(_, { budgetSubjectName: nameJa, }))));
    try {
      await logCsvExport({ companyId: company.id, modelName: 'monthlyBudgets', payload: { period, targetMonth, budgetSubjects: (isAll ? [{ name: '全部門', }] : selectedBudgetSubjects.map(_ => omit(_, 'ref'))), } });
      fileDownload(fileContent, `予実月次推移_${targetMonth}.csv`);
    } catch (e) {
      console.error(e);
      toast.error('失敗しました');
    }
    toggleExporting(false);
  };
  // NOTE: タブが切り替わったあと、budgetSubjectsが一旦空になるまで1周待つ必要がある。非同期にすれば解決するので、useDebounceを用いる。
  useDebounce(() => {
    const path = fullPathWithParams({
      dimension: dimension || 'none',
      budgetSubjectIds: budgetSubjectIds || (queryParams.budgetSubjectId && [queryParams.budgetSubjectId]) || [get(sortedBudgetSubjects, [0, 'id'])],
      budgetContainerId: budgetContainers.some(_ => _.id === budgetContainerId) ? budgetContainerId : get(budgetContainers, [0, 'id']),
      ...queryParams,
    }, location);
    history.replace(encodeURI(path));
  }, 1000, [tabKey, budgetContainers, budgetSubjects]);
  useEffect(() => {
    toggleInitiallyCommentRow(rows);
    onRowsChanged();
  }, [rows]);

  return (
    <div className="company-monthly-budgets">
      <div className="px-3 py-3">
        <div className="d-flex justify-content-end mb-2">
          <HelpLink text="予実管理を行う" />
        </div>
        <div className="mb-2">
          <h3 className="text-center">予実月次推移</h3>
        </div>
        <Nav tabs>
          {
            entries(enabledBudgetSubjectTypes(company)).flatMap(([budgetSubjectType, { label: budgetSubjectTypeLabel }]) => {
              return entries(pick(documentTypes, ['pl', 'cr'])).map(([documentType, { label: documentTypeLabel }]) => {
                const key = [budgetSubjectType, documentType].join('__');
                const label = [budgetSubjectTypeLabel, documentTypeLabel].join(' ');
                return (
                  <NavItem key={key}>
                    <NavLink className={classnames('cursor-pointer', { active: tabKey === key })} onClick={toggleTab.bind(null, key)}>
                      {label}
                    </NavLink>
                  </NavItem>
                );
              });
            })
          }
        </Nav>
        <div className="py-3">
          <div className="mb-3 d-flex justify-content-between align-items-center">
            <TrialQueriesSection 
              company={company}
              screenType="monthlyBudgets"
              uncontrols={screenQueryUncontrols.monthlyBudgets}
              items={[
                [
                  {
                    name: 'budgetSubjectIds',
                    type: 'multiSelect',
                    options: budgetSubjectOptions,
                    label: budgetSubjectTypeLabel,
                    isFront: true,
                    props: { width: 200, selectorProps: { isClearable: false } },
                  },
                  {
                    name: 'metrics',
                    type: 'multiSelect',
                    options: displayMetricOptions,
                    label: '数値項目',
                    props: { width: 200, }
                  },
                  {
                    name: 'itemNamesForFilter',
                    type: 'multiSelect',
                    options: itemNameOptions,
                    label: '科目名',
                    isFront: true,
                    props: { width: 300, }
                  },
                  {
                    name: 'dimension',
                    type: 'select',
                    options: dimensionOptions,
                    label: '明細',
                    isFront: true,
                    immediate: true,
                    props: { width: 150, selectorProps: { isClearable: false } },
                  },
                ],
                [
                  ...(
                    dimension !== 'none' ? [
                      {
                        name: 'isSubFilter',
                        type: 'boolean',
                        label: '明細単位で検索する',
                        immediate: true,
                      },
                    ] : []
                  ),
                  ...(
                    dimension !== 'none' && queryParams.isSubFilter === '1' ? [
                      {
                        name: 'subItemNamesForFilter',
                        type: 'multiSelect',
                        options: subItemNameOptions,
                        label: '明細名',
                        props: { width: 300, }
                      },
                    ] : []
                  ),
                ],
                [
                  {
                    name: 'budgetContainerId',
                    type: 'select',
                    options: budgetContainerOptions,
                    label: '予算',
                    isFront: true,
                    props: { width: 250, },
                  },
                  {
                    name: 'comparedBudgetContainerId',
                    type: 'select',
                    options: budgetContainerOptions,
                    label: '比較予算',
                    isFront: true,
                    props: { width: 250, },
                  },
                  {
                    name: 'displayColumnTypes',
                    type: 'multiSelect',
                    options: displayColumnTypeOptions,
                    label: '表示列',
                    isFront: true,
                    props: { width: 250, },
                  },
                ],
                ...entries(displayMetrics).map(([metric, { label, isRate }]) => {
                  return [
                    {
                      name: metric + 'Min',
                      type: 'numeric',
                      label: label + '下限' + (isRate ? '(%)' : ''),
                      props: { width: 200, }
                    },
                    {
                      name: metric + 'Max',
                      type: 'numeric',
                      label: label + '上限' + (isRate ? '(%)' : ''),
                      props: { width: 200, }
                    },
                  ];
                })
              ]}
            />
            <div className="d-flex align-items-center gap-1 text-nowrap">
              <BudgetButton {...{ period, company, accountItems, customAccountItems, }} onChangeBudgets={updateScreenVersion} />
              <Button color="secondary" onClick={onClickExport.bind(null, true)} disabled={isExporting}>
                全部門
                <span className={classnames('ml-1 fas', { 'fa-spin fa-spinner': isExporting, 'fa-download': !isExporting })} />
              </Button>
              <Button color="secondary" onClick={onClickExport.bind(null, false)} disabled={isExporting}>
                <span className={classnames('fas', { 'fa-spin fa-spinner': isExporting, 'fa-download': !isExporting })} />
              </Button>
              <ShareButton screenType="monthlyBudgets" {...{ company, subjectType: budgetSubjectType, subjects: selectedBudgetSubjects, }} />
              <CommentsDropdown company={company} companyId={companyId} currentUser={user} comments={comments} usersById={keyBy(members, 'id')} onClickComment={onClickComment(rows)} />
            </div>
          </div>
          <div className="d-flex justify-content-center position-relative" style={{ zIndex: 0 }}>
            <Table className="sticky-table table-hover" style={{ width: 'auto' }}>
              <thead className="text-center" style={{ lineHeight: '20px' }}>
                <tr>
                  <th className="text-left border-right p-0">
                    <ResizableBox className="p-2 d-flex align-items-center" width={COLUMN_WIDTH * 2.5} height={44} axis="x" resizeHandles={['e']} onResize={(e, _) => setLeftColumnWidth(_.size.width)}>
                      {
                        dimension !== 'none' && (
                          <AllCollapseButton size="sm" className="small px-0" color="link" rows={filteredRows} visibilities={subRowsVisibilities} setVisibilities={setAllSubRowsVisibilities} />
                        )
                      }
                      &nbsp;
                    </ResizableBox>
                  </th>
                  {
                    chunk(chunk(monthsItems[1], 3), 2).map((halfQuarters, halfIndex) => {
                      return (
                        <Fragment key={halfIndex}>
                          {
                            halfQuarters.map((monthItems, quarterIndex) => {
                              return (
                                <Fragment key={quarterIndex}>
                                  {
                                    (isEmpty(displayColumnTypes) || displayColumnTypes.includes('month')) && monthItems.map(({ closingDate, exists }, i) => {
                                      return (
                                        <th key={i} className={classnames('text-nowrap', { 'table-success': exists, 'table-danger': !exists })} style={{ width: COLUMN_WIDTH }}>
                                          <Link to={`/companies/${companyId}/monthBudgets?${qs.stringify({ period, budgetSubjectIds, targetMonth: formatDate(closingDate, 'YYYYMM'), tabKey: [budgetSubjectType, documentType].join('__'), })}`}>
                                            {formatDate(closingDate, 'YYYY/MM')}
                                          </Link>
                                          <JournalsSyncButton size="sm" className="small ml-1 px-0" color="link" company={company} period={period} targetMonth={formatDate(closingDate, 'YYYYMM')} />
                                        </th>
                                      );
                                    })
                                  }
                                  {
                                    (isEmpty(displayColumnTypes) || displayColumnTypes.includes('quarter')) && (
                                      <th className={classnames('text-nowrap')} style={{ width: COLUMN_WIDTH }}>
                                        {halfIndex * 2 + quarterIndex + 1}Q
                                      </th>
                                    )
                                  }
                                </Fragment>
                              );
                            })
                          }
                          {
                            (isEmpty(displayColumnTypes) || displayColumnTypes.includes('half')) && (
                              <th className={classnames('text-nowrap')} style={{ width: COLUMN_WIDTH }}>
                                {({ 0: '上', 1: '下' })[halfIndex]}半期
                              </th>
                            )
                          }
                        </Fragment>
                      );
                    })
                  }
                  <th style={{ width: COLUMN_WIDTH }}>合計</th>
                </tr>
              </thead>
              <tbody>
                {
                  filteredRows.map((row) => {
                    const { isSubRow, itemKey, } = row;
                    const showsSubRows = dimension !== 'none' && !!subRowsVisibilities[itemKey];
                    if(isSubRow && !showsSubRows) return null;

                    const breakdownItemsLimitRate = subRowsBreakdownItemsLimitRates[itemKey] || 1;
                    const toggleSubRows = setSubRowsVisibilities.bind(null, itemKey, !showsSubRows);
                    const showMoreSubRows = setSubRowsBreakdownItemsLimitRates.bind(null, itemKey, breakdownItemsLimitRate + 1);
                    return <Row pageProps={props} row={row} {...{ company, commentsGroupedByCommenteeKey, notesByKey, budgetSubjectType, setHoveredCommenters, metrics: displayMetrics, toggleSubRows, breakdownItemsLimitRate, showsSubRows, showMoreSubRows, leftColumnWidth, }} />
                  })
                }
              </tbody>
            </Table>
            <OverlayLoading isOpen={isSettingRows} />
          </div>
        </div>
      </div>
    </div>
  );
}
